import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { getMessages } from '../utils/translations';

export type Locale = 'pl-PL' | 'en-EN' | 'de-DE';

export type TranslationMessages = Record<string, string>;

interface LanguageContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  translate: (key: string, values?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const savedLocale = localStorage.getItem('locale');
    return (savedLocale as Locale) || 'pl-PL';
  });

  useEffect(() => {
    localStorage.setItem('locale', locale);
  }, [locale]);

  const setLocale = (newLocale: Locale) => {
    setLocaleState(newLocale);
  };

  const translate = (key: string, values?: Record<string, string | number>): string => {
    const messages = getMessages(locale) as TranslationMessages;
    let text = messages[key] || key;

    if (values) {
      Object.entries(values).forEach(([k, value]) => {
        text = text.replace(new RegExp(`{${k}}`, 'g'), String(value));
      });
    }

    return text;
  };

  return (
    <LanguageContext.Provider value={{ locale, setLocale, translate }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  
  return context;
}; 